/**
 * index.js / App Router Configuration
 *
 * Main entry point for the React application.
 * Responsible for:
 * - Rendering the React app into the DOM
 * - Configuring client-side routing using react-router-dom
 * - Defining public and protected routes
 *
 * Routing Structure:
 * - Public Routes:
 *   - "/" → Login page (default entry point)
 *   - "/signup" → User registration
 *   - "/marketing" → Marketing/landing page
 *
 * - Protected Routes (wrapped in Layout component):
 *   - Dashboard, sports pages, predictions, favorites, settings
 *   - Soccer and football league/team pages
 *   - Game modes (memory, trivia, guess player)
 *
 * Notes:
 * - Layout acts as a wrapper for authenticated pages (navbar, auth checks, etc.)
 * - Routes are currently defined statically (large file — potential for refactor)
 * - Consider dynamic routing or route mapping for scalability
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import {HashRouter, Routes, Route} from 'react-router-dom';

// Layout (protected wrapper)
import Layout from './Layout';

// Core pages
import Home from './pages/Home';
import Sports from './pages/Sports';
import Predictions from './pages/Predictions';
import Favorites from './pages/Favorites';
import Settings from './pages/Settings';
import Teams from './pages/Teams';
import Trivia from './pages/Trivia';

// Auth + Marketing
import SignUp from './pages/SignUp';
import Marketing from './pages/Marketing';

// Soccer Pages
import PremierLeagueHome from './pages/soccer/HomePremierLeague';
import PremierLeagueMatches from './pages/soccer/PremierLeagueMatches';
import PremierLeagueTeams from './pages/soccer/PremierLeagueTeams';
import SoccerLeagues from './pages/soccer/SoccerLeagues';
import SoccerGuessPlayer from './pages/SoccerGuessPlayer';
import SoccerGames from './pages/SoccerGames';
import SoccerMemory from './pages/SoccerMemory';

// Football Pages
import FootballLeagues from './pages/football/FootballLeagues'
import FootballGuessPlayer from './pages/FootballGuessPlayer';
import FootballGames from './pages/FootballGames';
import FootballTrivia from './pages/FootballTrivia';
import FootballMemory from './pages/FootballMemory';
import AFChomePage from './pages/football/AFChomePage';
import NFChomePage from './pages/football/NFChomePage';
import NfcTeamsPage from './pages/football/nfcTeamsPage';
import AfcTeamsPage from './pages/football/afcTeamsPage';

// Premier League Team Pages
import ArsenalHome from './pages/soccer/PLteampages/ArsenalHome';
import AstonVillaHome from './pages/soccer/PLteampages/AstonVillaHome';
import BournemouthHome from './pages/soccer/PLteampages/BournemouthHome';
import BrentfordHome from './pages/soccer/PLteampages/BrentfordHome';
import BrightonHoveAlbionHome from './pages/soccer/PLteampages/BrightonHoveAlbionHome';
import BurnleyHome from './pages/soccer/PLteampages/BurnleyHome';
import ChelseaHome from './pages/soccer/PLteampages/ChelseaHome';
import CrystalPalaceHome from './pages/soccer/PLteampages/CrystalPalaceHome';
import EvertonHome from './pages/soccer/PLteampages/EvertonHome';
import FulhamHome from './pages/soccer/PLteampages/FulhamHome';
import LeedsHome from './pages/soccer/PLteampages/LeedsHome';
import LiverpoolHome from './pages/soccer/PLteampages/LiverpoolHome';
import ManchesterCityHome from './pages/soccer/PLteampages/ManchesterCityHome';
import ManchesterUnitedHome from './pages/soccer/PLteampages/ManchesterUnitedHome';
import NewcastleHome from './pages/soccer/PLteampages/NewcastleHome';
import NottinghamHome from './pages/soccer/PLteampages/NottinghamHome';
import SunderlandHome from './pages/soccer/PLteampages/SunderlandHome';
import TottenhamHome from './pages/soccer/PLteampages/TottenhamHome';
import WestHamHome from './pages/soccer/PLteampages/WestHamHome';
import WolverhamptonHome from './pages/soccer/PLteampages/WolverhamptonHome';

// NFL Team Pages
import BroncosHome from './pages/football/AFCteampages/BroncosHome';
import PatriotsHome from './pages/football/AFCteampages/PatriotsHome';
import JaguarsHome from './pages/football/AFCteampages/JaguarsHome';
import SteelersHome from './pages/football/AFCteampages/SteelersHome';
import TexansHome from './pages/football/AFCteampages/TexansHome';
import BillsHome from './pages/football/AFCteampages/BillsHome';
import ChargersHome from './pages/football/AFCteampages/ChargersHome';
import ColtsHome from './pages/football/AFCteampages/ColtsHome';
import RavensHome from './pages/football/AFCteampages/RavensHome';
import DolphinsHome from './pages/football/AFCteampages/DolphinsHome';
import BengalsHome from './pages/football/AFCteampages/BengalsHome';
import ChiefsHome from './pages/football/AFCteampages/ChiefsHome';
import BrownsHome from './pages/football/AFCteampages/BrownsHome';
import RaidersHome from './pages/football/AFCteampages/RaidersHome';
import JetsHome from './pages/football/AFCteampages/JetsHome';
import TitansHome from './pages/football/AFCteampages/TitansHome';
import SeahawksHome from './pages/football/NFCteampages/SeahawksHome';
import BearsHome from './pages/football/NFCteampages/BearsHome';
import EaglesHome from './pages/football/NFCteampages/EaglesHome';
import PanthersHome from './pages/football/NFCteampages/PanthersHome';
import RamsHome from './pages/football/NFCteampages/RamsHome';
import FortyNinersHome from './pages/football/NFCteampages/49ersHome';
import PackersHome from './pages/football/NFCteampages/PackersHome';
import VikingsHome from './pages/football/NFCteampages/VikingsHome';
import LionsHome from './pages/football/NFCteampages/LionsHome';
import BuccaneersHome from './pages/football/NFCteampages/BuccaneersHome';
import FalconsHome from './pages/football/NFCteampages/FalconsHome';
import CowboysHome from './pages/football/NFCteampages/CowboysHome';
import SaintsHome from './pages/football/NFCteampages/SaintsHome';
import CommandersHome from './pages/football/NFCteampages/CommandersHome';
import GiantsHome from './pages/football/NFCteampages/GiantsHome';
import CardinalsHome from './pages/football/NFCteampages/CardinalsHome';


const App = () =>{
  return(
    <HashRouter>
      <Routes>
        
        {/**
         * Public Routes
         * Accessible without authentication
         */}
        <Route path="/" element={<Marketing />} /> {/* Changed default route to Login */}
        <Route path="/marketing" element={<Marketing />} /> {/* Moved Marketing to a separate route */}
        <Route path="/signup" element={<SignUp />} /> 

        {/**
         * Protected Routes
         * Wrapped in Layout (handles shared UI + potential auth logic)
         */}
        <Route element={<Layout />}>
          <Route path="/home" element={<Home />} />
          <Route path="/matches" element={<SoccerLeagues />} />
          <Route path="/teams" element={<Teams />} />
          <Route path="/predictions" element={<Predictions />} />
          <Route path="/favorites" element={<Favorites />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/sports" element={<Sports />} />
          <Route path="/soccer-games" element={<SoccerGames />} />
          <Route path="/football-games" element={<FootballGames />} />
          <Route path="/football-guess-player" element={<FootballGuessPlayer />} />
          <Route path='/soccer-guess-player' element={<SoccerGuessPlayer />} />
          <Route path="/sports/soccer" element={<SoccerLeagues />} />
          <Route path="/sports/football" element={<FootballLeagues />} />
          <Route path="/sports/soccer/premier-league" element={<PremierLeagueHome />} />
          <Route path="/matches/premier-league" element={<PremierLeagueMatches />} />
          <Route path="/teams/premier-league" element={<PremierLeagueTeams />} />
          <Route path="/trivia" element={<Trivia />} />
          <Route path="/football-trivia" element={<FootballTrivia />} />
          <Route path="/teams/premier-league/arsenal" element={<ArsenalHome />} />
          <Route path="/teams/premier-league/aston-villa" element={<AstonVillaHome />} />
          <Route path="/teams/premier-league/bournemouth" element={<BournemouthHome />} />
          <Route path="/teams/premier-league/brentford" element={<BrentfordHome />} />
          <Route path="/teams/premier-league/brighton-hove-albion" element={<BrightonHoveAlbionHome />} />
          <Route path="/teams/premier-league/burnley" element={<BurnleyHome />} />
          <Route path="/teams/premier-league/chelsea" element={<ChelseaHome />} />
          <Route path="/teams/premier-league/crystal-palace" element={<CrystalPalaceHome />} />
          <Route path="/teams/premier-league/everton" element={<EvertonHome />} />
          <Route path="/teams/premier-league/fulham" element={<FulhamHome />} />
          <Route path="/teams/premier-league/leeds-united" element={<LeedsHome />} />
          <Route path="/teams/premier-league/liverpool" element={<LiverpoolHome />} />
          <Route path="/teams/premier-league/manchester-city" element={<ManchesterCityHome />} />
          <Route path="/teams/premier-league/manchester-united" element={<ManchesterUnitedHome />} />
          <Route path="/teams/premier-league/newcastle-united" element={<NewcastleHome />} />
          <Route path="/teams/premier-league/nottingham-forest" element={<NottinghamHome />} />
          <Route path="/teams/premier-league/sunderland" element={<SunderlandHome />} />
          <Route path="/teams/premier-league/tottenham-hotspur" element={<TottenhamHome />} />
          <Route path="/teams/premier-league/west-ham-united" element={<WestHamHome />} />
          <Route path="/teams/premier-league/wolverhampton-wanderers" element={<WolverhamptonHome />} />
          <Route path="/sports/football/afc" element={<AFChomePage />} />
          <Route path="/sports/football/nfc" element={<NFChomePage />} />
          <Route path="/sports/football/afc/teams" element={<AfcTeamsPage />} />
          <Route path="/sports/football/nfc/teams" element={<NfcTeamsPage />} />
          <Route path="/sports/football/afc/teams/denver-broncos" element={<BroncosHome />} />
          <Route path="/sports/football/afc/teams/new-england-patriots" element={<PatriotsHome />} />
          <Route path="/sports/football/afc/teams/jacksonville-jaguars" element={<JaguarsHome />} />
          <Route path="/sports/football/afc/teams/pittsburgh-steelers" element={<SteelersHome />} />
          <Route path="/sports/football/afc/teams/houston-texans" element={<TexansHome />} />
          <Route path="/sports/football/afc/teams/buffalo-bills" element={<BillsHome />} />
          <Route path="/sports/football/afc/teams/los-angeles-chargers" element={<ChargersHome />} />
          <Route path="/sports/football/afc/teams/indianapolis-colts" element={<ColtsHome />} />
          <Route path="/sports/football/afc/teams/baltimore-ravens" element={<RavensHome />} />
          <Route path="/sports/football/afc/teams/miami-dolphins" element={<DolphinsHome />} />
          <Route path="/sports/football/afc/teams/cincinnati-bengals" element={<BengalsHome />} />
          <Route path="/sports/football/afc/teams/kansas-city-chiefs" element={<ChiefsHome />} />
          <Route path="/sports/football/afc/teams/cleveland-browns" element={<BrownsHome />} />
          <Route path="/sports/football/afc/teams/las-vegas-raiders" element={<RaidersHome />} />
          <Route path="/sports/football/afc/teams/new-york-jets" element={<JetsHome />} />
          <Route path="/sports/football/afc/teams/tennessee-titans" element={<TitansHome />} />
          <Route path="/sports/football/nfc/teams/seattle-seahawks" element={<SeahawksHome />} />
          <Route path="/sports/football/nfc/teams/chicago-bears" element={<BearsHome />} />
          <Route path="/sports/football/nfc/teams/philadelphia-eagles" element={<EaglesHome />} />
          <Route path="/sports/football/nfc/teams/carolina-panthers" element={<PanthersHome />} />
          <Route path="/sports/football/nfc/teams/los-angeles-rams" element={<RamsHome />} />
          <Route path="/sports/football/nfc/teams/san-francisco-49ers" element={<FortyNinersHome />} />
          <Route path="/sports/football/nfc/teams/green-bay-packers" element={<PackersHome />} />
          <Route path="/sports/football/nfc/teams/minnesota-vikings" element={<VikingsHome />} />
          <Route path="/sports/football/nfc/teams/detroit-lions" element={<LionsHome />} />
          <Route path="/sports/football/nfc/teams/tampa-bay-buccaneers" element={<BuccaneersHome />} />
          <Route path="/sports/football/nfc/teams/atlanta-falcons" element={<FalconsHome />} />
          <Route path="/sports/football/nfc/teams/dallas-cowboys" element={<CowboysHome />} />
          <Route path="/sports/football/nfc/teams/new-orleans-saints" element={<SaintsHome />} />
          <Route path="/sports/football/nfc/teams/washington-commanders" element={<CommandersHome />} />
          <Route path="/sports/football/nfc/teams/new-york-giants" element={<GiantsHome />} />
          <Route path="/sports/football/nfc/teams/arizona-cardinals" element={<CardinalsHome />} />
          <Route path="/football-memory-game" element={<FootballMemory />} />
          <Route path="/soccer-memory-game" element={<SoccerMemory />} />
        </Route>
      </Routes>
    </HashRouter>
  );
};

/**
 * Application Entry Point
 *
 * Mounts the React application to the DOM element with id "root".
 * React.StrictMode helps identify potential problems during development.
 */
const root = ReactDOM.createRoot(document.getElementById('root'));

root.render(
    <React.StrictMode>
    <App />
  </React.StrictMode>
);
